import type { OpenParameter } from '../../../types/apiReturnType'
import { zeroAddress } from 'viem'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import type { LlamaLendMarketsRaw } from './types'

/**
 * Synthesized per-market lender key, e.g.
 * `LLAMALEND_4F79FE450A2BAF833E8F50340BD230F5A3ECAFE9` (= the sreUSD/crvUSD
 * market). Keyed by the CONTROLLER, which is what every write and every user
 * read targets — the vault is a lookup off it. Address-suffixed
 * (Teller/Exactly/Inverse convention); the chain id is not part of the key
 * because chain scoping happens at the marketUid level.
 *
 * Both generations share this key space on purpose: to a user they are one
 * protocol, and the Curve API returns them in one list. The `version` field on
 * the market row is what encoders branch on.
 */
export function llamaLendLenderKey(lender: string, controller: string): string {
  return `${lender}_${controller.replace(/^0x/i, '').toUpperCase()}`
}

/** Recover `{ lender, controller }` from a per-market key (or undefined). */
export function llamaLendKeyParts(
  key: string,
): { lender: string; controller: string } | undefined {
  if (!key.startsWith('LLAMALEND_')) return undefined
  const suffix = key.slice('LLAMALEND_'.length)
  if (!/^[0-9A-F]{40}$/.test(suffix)) return undefined
  return { lender: 'LLAMALEND', controller: '0x' + suffix.toLowerCase() }
}

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name: symbol, decimals }
}

/**
 * Map the LlamaLend batch into the shared `MorphoGeneralPublicResponse` shape,
 * keyed by `LLAMALEND_<CONTROLLER_ADDR>` — one key per market.
 *
 * Per market, two entries in the isolated-pair layout:
 *
 *  - COLLATERAL entry — deposit-only. `collateralFactor` is the LTV AT THE
 *    MARKET'S DEFAULT BAND COUNT, because LlamaLend has no market-constant
 *    LTV: it is a function of `N` and moves 0.886..0.991 on a single market.
 *    The whole curve rides along in `params.market.llamalend.bandLtv` so the
 *    UI can show the trade-off and the leverage sizer can use the real number
 *    for the `N` the user actually picks.
 *  - LOAN entry — the borrowed token. Supply side is the ERC-4626 vault, so
 *    unlike Inverse this one HAS `totalDeposits`.
 *
 * SOFT LIQUIDATION is the thing this shape cannot express natively, so it is
 * carried explicitly in the descriptor. `liquidationPenalty` here is the HARD
 * liquidation bonus only — it applies below the entire band range. Inside the
 * range a position is converted gradually through the market's own AMM with no
 * penalty at all, and a consumer that renders `liquidationPenalty` as "what
 * you lose when the price hits X" is describing the wrong event.
 */
/** `MIN_TICKS` / `MAX_TICKS` on the Controller — identical on v1 and v2. */
const LLAMALEND_MIN_BANDS = 4
const LLAMALEND_MAX_BANDS = 50

export function convertLlamaLendMarketsToResponse(
  raw: LlamaLendMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const cfg = raw?.config
  if (!cfg) return out

  for (const m of raw.markets ?? []) {
    const market = m.market
    const lenderKey = llamaLendLenderKey(raw.lender, market.controller)

    const collAddr = market.collateralToken.toLowerCase()
    const loanAddr = market.borrowedToken.toLowerCase()
    const collDecimals = market.collateralDecimals
    const loanDecimals = market.borrowedDecimals
    const collToken = tokens[collAddr]
    const loanToken = tokens[loanAddr]
    const collSymbol = collToken?.symbol ?? market.collateralSymbol ?? 'COLL'
    const loanSymbol = loanToken?.symbol ?? market.borrowedSymbol ?? 'LOAN'
    const marketName = market.name ?? `${loanSymbol} / ${collSymbol}`

    const collPriceKey =
      toOracleKey(collToken?.assetGroup!) ||
      toGenericPriceKey(collAddr, chainId)
    const loanPriceKey =
      toOracleKey(loanToken?.assetGroup!) ||
      toGenericPriceKey(loanAddr, chainId)
    // Prefer the global price oracle; the API's own USD quote is the fallback
    // so a chain-sourced batch (which has no USD leg) still prices.
    const collPrice = prices[collPriceKey] || m.collateralPriceUsd || 0
    const loanPrice = prices[loanPriceKey] || m.borrowedPriceUsd || 0

    const totalDebt = m.totalDebt ?? 0
    const totalDeposits = m.totalSupplied ?? 0
    const borrowLiquidity = m.borrowingEnabled ? (m.availableToBorrow ?? 0) : 0
    // Lender withdrawals are capped by the same idle balance that caps
    // borrowing — the vault cannot pay out what is currently lent out.
    const withdrawLiquidity = m.availableToBorrow ?? 0

    // LlamaLend's own denominator: debt over total reserves.
    const reserves = totalDebt + (m.availableToBorrow ?? 0)
    const utilization = reserves > 0 ? totalDebt / reserves : 0

    // Rates arrive as decimals from both sources; the response shape is percent.
    const borrowRatePct = (m.borrowApr ?? 0) * 100
    const depositRatePct = (m.lendApr ?? 0) * 100

    /**
     * LTV at the market's default band count, or NULL when the on-chain read
     * that derives it did not land (see `fetchChainExtras`).
     *
     * Deliberately NOT defaulted to 0. A LlamaLend market always has a real
     * LTV — it is a function of `loan_discount`, `A` and `N`, never zero — so
     * publishing 0 does not say "no LTV available", it asserts "this collateral
     * is worth nothing". Consumers cannot tell those apart, and every one of
     * them read it the wrong way: a live sDOLA/crvUSD position rendered with
     * `Max LTV —`, no collateral row and `Health 0.00` while sitting at ~95%
     * LTV. NULL propagates as "unknown" instead: nullable in `market_config`,
     * excluded from pair leverage by the existing
     * `COALESCE(borrow_collateral_factor, 0) > 0` guard — same treatment 0 got,
     * without the false claim.
     */
    const ltv = m.collateralFactor ?? null
    const liqPenalty = Number(market.liquidationDiscount) / 1e18 || 0
    // `liquidate` takes a `frac`, and a full close is the default — there is
    // no protocol-level partial-liquidation cap.
    const closeFactor = 1

    const depositsEnabled = (m.maxDeposit ?? 0) > 0

    /**
     * The band count is a real borrower choice, so the config advertises the
     * DOMAIN and the position carries the value (`modes[posId]`).
     *
     * `collateralFactor` above is one point on that curve — the value at
     * `defaultBands`. A consumer quoting a different `N` must recompute rather
     * than reuse it; measured live, ~21% of loans sit at an `N` other than the
     * default, so treating the single number as the position's LTV is wrong
     * for roughly one position in five.
     *
     * Identical on v1 and v2: `create_loan` asserts `initial_debt == 0` on
     * both, and `_add_collateral_borrow` reuses the existing tick width, so `N`
     * is one-per-market and immutable regardless of generation.
     */
    const bandParameter: OpenParameter = {
      kind: 'llamalend-bands',
      dimension: 'collateralFactor',
      domain: { min: LLAMALEND_MIN_BANDS, max: LLAMALEND_MAX_BANDS },
      default: market.defaultBands ?? 10,
      immutableAfterOpen: true,
      // The whole curve, so a consumer holding a position's ACTUAL `N` can
      // resolve the factor it implies instead of the default's — the config
      // lookup `configs[mode]` cannot work here (the mode slot carries a
      // parameter VALUE, not a config key), and before this rode along, that
      // miss fell through to factor 1: undiscounted collateral, health =
      // deposits/debt, and a max-loop quote the Controller would refuse.
      curve: m.bandLtv ?? undefined,
    }

    // @ts-ignore — built incrementally, matches the Inverse/River converters
    const entry: MorphoGeneralPublicResponse = { data: {} }

    // 1. COLLATERAL entry — deposit-only.
    const collUid = createMarketUid(chainId, lenderKey, collAddr)
    entry.data[collUid] = {
      marketUid: collUid,
      name: 'Collateral ' + collSymbol,
      poolId: market.controller.toLowerCase(),
      underlying: collAddr,
      asset: currencyFor(collAddr, collDecimals, collSymbol, tokens),
      // Collateral lives inside the LLAMMA spread over bands; there is no
      // single market-level balance worth reporting, and the number the UI
      // needs is the borrowed side anyway.
      totalDeposits: 0,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: 0,
      borrowLiquidity: 0,
      totalLiquidityUSD: 0,
      borrowLiquidityUSD: 0,
      totalDepositsUSD: 0,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      depositRate: 0,
      variableBorrowRate: 0,
      stableBorrowRate: 0,
      intrinsicYield:
        additionalYields?.intrinsicYields?.[collToken?.assetGroup!] ?? 0,
      rewards: undefined,
      decimals: collDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: ltv,
          collateralFactor: ltv,
          borrowFactor: 1,
          liquidationPenalty: liqPenalty,
          closeFactor,
          collateralDisabled: false,
          debtDisabled: true,
          openParameter: bandParameter,
        },
      },
      closeFactor,
      collateralActive: true,
      borrowingEnabled: false,
      depositsEnabled: m.borrowingEnabled,
      hasStable: false,
      isActive: true,
      isFrozen: false,
    } as any

    // 2. LOAN entry — the borrowed token, with a real ERC-4626 supply side.
    const loanUid = createMarketUid(chainId, lenderKey, loanAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: loanSymbol,
      poolId: market.controller.toLowerCase(),
      underlying: loanAddr,
      asset: currencyFor(loanAddr, loanDecimals, loanSymbol, tokens),
      totalDeposits,
      totalDebtStable: 0,
      totalDebt,
      totalLiquidity: withdrawLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: withdrawLiquidity * loanPrice,
      borrowLiquidityUSD: borrowLiquidity * loanPrice,
      totalDepositsUSD: totalDeposits * loanPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: totalDebt * loanPrice,
      utilization,
      depositRate: depositRatePct,
      variableBorrowRate: borrowRatePct,
      stableBorrowRate: 0,
      // Which curve the depth layer should use. Both are closed-form and both
      // policies also expose an exact on-chain `future_rate`, so rate-at-depth
      // never has to be modelled here.
      rateModel:
        market.rateModel ?? (market.version === 1 ? 'semilog' : 'hyperbolic'),
      intrinsicYield:
        additionalYields?.intrinsicYields?.[loanToken?.assetGroup!] ?? 0,
      rewards: additionalYields?.lenderRewards?.[lenderKey] ?? undefined,
      decimals: loanDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: liqPenalty,
          closeFactor,
          collateralDisabled: true,
          debtDisabled: !m.borrowingEnabled,
          // CURVE-LESS on purpose: this row's factors are constants (the
          // borrowed side never backs a loan, whatever `N` is), but the row
          // must still declare the market parameterized — a position's mode
          // slot carries the band count, and without this marker a
          // `resolveModeConfig` miss would fall through to the callers'
          // factor-1 fallback and count the LEND leg as full collateral.
          openParameter: { ...bandParameter, curve: undefined },
        },
      },
      closeFactor,
      collateralActive: false,
      borrowingEnabled: m.borrowingEnabled,
      depositsEnabled,
      hasStable: false,
      variableBorrowDisabled: !m.borrowingEnabled,
      isActive: true,
      isFrozen: false,
    } as any

    entry.params = {
      market: {
        lender: lenderKey,
        name: marketName,
        loanDecimals,
        collateralDecimals: collDecimals,
        // The Controller doubles as the market id.
        id: market.controller.toLowerCase(),
        // Mirrors the Morpho shape. Empty string, not "0": a consumer parsing
        // this as a number gets NaN and can branch, where "0" would silently
        // become a zero LTV. The real curve is in `llamalend.bandLtv`.
        lltv: ltv !== null ? String(ltv) : '',
        /**
         * The AMM, deliberately — LlamaLend's price feed is `price_oracle()` on
         * the LLAMMA itself, so that is the only address a reader can call.
         *
         * This used to read `market.priceOracle ?? market.amm`, but
         * `priceOracle` was never assigned anywhere in this pipeline (the name
         * is used elsewhere for the price VALUE, not the contract), so the
         * fallback was doing all the work. Naming it directly removes the trap:
         * a Curve `price_oracle_contract` exposes `price()` and NOT
         * `price_oracle()`, so populating that field would have silently
         * pointed every oracle reader at an interface it cannot call.
         */
        oracle: market.amm,
        irm: market.monetaryPolicy ?? zeroAddress,
        collateralAddress: collAddr,
        loanAddress: loanAddr,
        // --- LlamaLend descriptor (consumed by the calldata builders,
        // the leverage sizer and worker-api resolvers) ---
        llamalend: {
          /**
           * The market's LLAMMA, and the ONLY readable oracle for it:
           * `price_oracle()` lives here, quoted in the BORROWED token.
           *
           * Carried on the descriptor — not only under `addresses` — because
           * this object is what consumers persist verbatim (yield-tracer
           * serialises it into the `llamalend` column of the shared isolated-
           * market table) and the oracle-price path resolves the AMM from it.
           * Omitting it does not fail loudly: the reader finds `undefined`,
           * skips the market, and the lender ends up with NO prices at all
           * while every row still looks present and correct.
           *
           * Do not "clean this up" as a duplicate of `addresses.amm` — the two
           * are read by different consumers, and only this one survives into
           * the descriptor column.
           */
          amm: market.amm,
          /** 1 = `oneway` (v1), 2 = `oneway-v2`. Branch every encoder on this. */
          version: market.version,
          /** FAIL CLOSED — an unknown market is not delegatable. */
          supportsDelegation: market.supportsDelegation === true,
          /**
           * The market's index in its factory — also the `controller_id` that
           * Curve's leverage zap requires in `callback_args`. Without it no
           * leveraged transaction can be encoded.
           */
          factoryIndex: market.factoryIndex,
          /**
           * Whether Curve's deployed v1 zap accepts this market. A WIDER gate
           * than `supportsDelegation` — leverage is EOA-direct and needs no
           * grant — so never conflate the two.
           */
          supportsLeverage: market.supportsLeverage === true,
          /**
           * Band count the reported `collateralFactor` was computed at. A
           * consumer quoting a different `N` MUST re-read `bandLtv`.
           */
          defaultBands: market.defaultBands ?? 10,
          /** `{ [N]: ltv }` — the collateral-factor curve across band counts. */
          bandLtv: m.bandLtv ?? undefined,
          /** AMM band-geometry parameter. */
          ammA: market.ammA,
          loanDiscount: market.loanDiscount,
          /** HARD-liquidation bonus only; soft liquidation carries no penalty. */
          liquidationDiscount: market.liquidationDiscount,
          /**
           * Positions soft-liquidate over a band RANGE rather than at a single
           * price. `activeBand` says whether the market is currently
           * converting anyone; the per-user range comes from `user_prices`.
           */
          activeBand: m.activeBand ?? undefined,
          nLoans: m.nLoans ?? undefined,
          /** Collateral price in BORROWED-token terms (the AMM's EMA oracle). */
          collateralPrice:
            m.collateralPrice !== null ? String(m.collateralPrice) : undefined,
          /** v2 only; `0` means borrowing is switched off. */
          borrowCap: m.borrowCap !== null ? String(m.borrowCap) : undefined,
          /**
           * Assets per 1e18 vault shares — what a lender's share balance is
           * worth. Published because the SUPPLY side of a LlamaLend market is
           * an ERC-4626 vault whose shares run ~1000x the asset scale, so a
           * raw balance is meaningless without it.
           */
          pricePerShare:
            m.pricePerShare !== null ? String(m.pricePerShare) : undefined,
          addresses: {
            controller: market.controller,
            vault: market.vault,
            /**
             * MUST be excluded from any leverage swap route — the Controller
             * asserts this AMM's band state is unchanged across a callback,
             * so a route through it reverts with no usable reason string.
             */
            amm: market.amm,
            monetaryPolicy: market.monetaryPolicy,
            /**
             * Curve's deployed v1 leverage zaps and the aggregator routers
             * they are hard-wired to. We route leverage through these rather
             * than deploying a callbacker: their router is an immutable with
             * no allowlist, so arbitrary aggregator calldata is permitted.
             *
             * A quote MUST be checked against the matching `*Router` before
             * encoding — the zap forwards mismatched calldata to its own
             * router rather than rejecting it.
             */
            leverageZapOdos: cfg.leverageZapOdos,
            leverageZapOdosRouter: cfg.leverageZapOdosRouter,
            leverageZap1inch: cfg.leverageZap1inch,
            leverageZap1inchRouter: cfg.leverageZap1inchRouter,
            oneWayFactory: cfg.oneWayFactory,
            lendFactory: cfg.lendFactory,
          },
        },
      } as any,
    }
    entry.chainId = chainId

    out[lenderKey] = entry
  }

  return out
}
